import { motion } from 'framer-motion';
import { Check, BookOpen, Zap, Crown, Star, ExternalLink, ArrowRight, MessageCircle } from 'lucide-react';

// PasswordStrengthMeter - Vercel/Linear style
export const PasswordStrengthMeter = ({ password }: { password: string }) => {
  const calculatePasswordStrength = (pwd: string) => {
    let strength = 0;
    const checks = {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
    };
    if (checks.length) strength++;
    if (checks.uppercase) strength++;
    if (checks.lowercase) strength++;
    if (checks.number) strength++;
    return { strength, checks };
  };

  const { strength, checks } = calculatePasswordStrength(password);
  if (password.length === 0) return null;

  if (strength === 4) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-2"
      >
        <div className="flex items-center gap-1.5 text-[12px] text-emerald-600 dark:text-emerald-400">
          <Check className="size-3.5" strokeWidth={2} />
          Güçlü şifre
        </div>
      </motion.div>
    );
  }

  return (
    <div className="mt-2.5 space-y-2.5">
      {/* Strength bars */}
      <div className="flex h-[3px] w-full gap-1">
        {[1, 2, 3, 4].map((level) => (
          <div
            key={level}
            className={`h-full flex-1 rounded-full transition-all duration-300 ${
              strength >= level
                ? strength <= 2
                  ? 'bg-red-400'
                  : strength <= 3
                    ? 'bg-amber-400'
                    : 'bg-emerald-400'
                : 'bg-[#e5e5e5] dark:bg-[#333]'
            }`}
          />
        ))}
      </div>
      {/* Criteria */}
      <div className="grid grid-cols-2 gap-1.5">
        {[
          { check: checks.length, label: 'En az 8 karakter' },
          { check: checks.uppercase, label: 'Büyük harf' },
          { check: checks.lowercase, label: 'Küçük harf' },
          { check: checks.number, label: 'Sayı' },
        ].map(({ check, label }) => (
          <div
            key={label}
            className={`flex items-center gap-1.5 text-[11px] transition-colors ${
              check
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-[#999] dark:text-[#555]'
            }`}
          >
            <div
              className={`size-[6px] rounded-full transition-colors ${
                check ? 'bg-emerald-500' : 'bg-[#d4d4d4] dark:bg-[#444]'
              }`}
            />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

// Paket verileri
const packages = [
  {
    id: 'hizli-okur',
    name: 'Hızlı Okur',
    subtitle: 'Tüm Yaş Grupları',
    price: '4.999',
    icon: Zap,
    paymentUrl: 'https://www.paytr.com/link/9KBikbf',
    features: [
      'Okuma hızını 2 kat artır',
      'Basılı Egzersiz Kitapçığı',
      'Günlük Egzersiz Planı',
      'İlerleme Raporları',
    ],
    popular: false,
  },
  {
    id: 'basic',
    name: 'Okur Atölyesi Basic',
    subtitle: 'LGS',
    price: '6.999',
    icon: BookOpen,
    paymentUrl: 'https://www.paytr.com/link/ki9MlWe',
    features: [
      'Yapay Zeka Destekli Analiz',
      'AI Sohbet Botu',
      'Hızlı Okur + Egzersizler',
      'Konu & Soru Atölyesi',
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Okur Atölyesi Pro',
    subtitle: 'LGS',
    price: '13.999',
    icon: Crown,
    paymentUrl: 'https://www.paytr.com/link/pro',
    features: [
      'Basic paketindeki her şey',
      'LGS için Basılı 6 Föy',
      'Okur ile İlerle Föy Seti',
      'Özel Video Dersleri',
    ],
    popular: true,
  },
];

interface SignUpFormProps {
  isLoading?: boolean;
  onSubmit?: (data: any) => void;
}

export const SignUpForm = ({}: SignUpFormProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col gap-3.5"
    >
      {/* Paket kartları */}
      {packages.map((pkg, index) => {
        const Icon = pkg.icon;
        const isPopular = pkg.popular;

        return (
          <motion.a
            key={pkg.id}
            href={pkg.paymentUrl}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="group relative block"
          >
            {/* Popüler kart arkasındaki glow efekti */}
            {isPopular && (
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-[#1bb8f5] via-[#da2992] to-[#1bb8f5] opacity-[0.35] blur-sm transition-opacity duration-500 group-hover:opacity-60" />
            )}

            <div
              className={`
                relative overflow-hidden rounded-2xl border transition-all duration-300
                ${isPopular
                  ? 'border-white/20 dark:border-white/10 bg-gradient-to-br from-[#0c1a2e]/95 via-[#0f1f35]/95 to-[#141e30]/95 dark:from-[#0c1a2e] dark:via-[#0f1f35] dark:to-[#141e30] shadow-xl shadow-[#1bb8f5]/10'
                  : 'border-[#e5e5e5]/80 dark:border-[#2a2a2a] bg-white/70 dark:bg-[#111]/70 backdrop-blur-xl hover:border-[#1bb8f5]/30 dark:hover:border-[#1bb8f5]/20 hover:shadow-lg hover:shadow-[#1bb8f5]/5'
                }
              `}
            >
              {/* Glassmorphism overlay - popüler kart için */}
              {isPopular && (
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#1bb8f5]/10 via-transparent to-[#da2992]/5" />
              )}

              {/* İçerik */}
              <div className="relative p-5">
                {/* Üst satır: Badge + Fiyat */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {/* İkon */}
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105 ${
                        isPopular
                          ? 'bg-[#1bb8f5]/15 ring-1 ring-[#1bb8f5]/20'
                          : 'bg-[#f5f5f5] dark:bg-[#1a1a1a] ring-1 ring-[#e5e5e5]/50 dark:ring-[#333]/50'
                      }`}
                    >
                      <Icon
                        className={`h-[18px] w-[18px] ${
                          isPopular ? 'text-[#1bb8f5]' : 'text-[#666] dark:text-[#888]'
                        }`}
                        strokeWidth={1.5}
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <h3
                          className={`text-[14px] font-semibold tracking-tight ${
                            isPopular ? 'text-white' : 'text-[#0a0a0a] dark:text-[#ededed]'
                          }`}
                        >
                          {pkg.name}
                        </h3>
                        {isPopular && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#1bb8f5]/20 to-[#da2992]/20 px-2.5 py-0.5 text-[10px] font-semibold text-[#1bb8f5] ring-1 ring-[#1bb8f5]/25 backdrop-blur-sm">
                            <Star className="h-2.5 w-2.5 fill-[#1bb8f5]" />
                            Popüler
                          </span>
                        )}
                      </div>
                      <p
                        className={`mt-0.5 text-[11px] ${
                          isPopular ? 'text-white/50' : 'text-[#999] dark:text-[#666]'
                        }`}
                      >
                        {pkg.subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Fiyat */}
                  <div className="text-right">
                    <div className="flex items-baseline">
                      <span
                        className={`text-[22px] font-bold tracking-tight tabular-nums ${
                          isPopular ? 'text-white' : 'text-[#0a0a0a] dark:text-white'
                        }`}
                      >
                        {pkg.price}
                      </span>
                      <span
                        className={`ml-0.5 text-[12px] font-medium ${
                          isPopular ? 'text-white/40' : 'text-[#999] dark:text-[#555]'
                        }`}
                      >
                        ₺
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ayırıcı çizgi */}
                <div
                  className={`my-4 h-px ${
                    isPopular
                      ? 'bg-gradient-to-r from-transparent via-white/10 to-transparent'
                      : 'bg-gradient-to-r from-transparent via-[#e5e5e5] dark:via-[#2a2a2a] to-transparent'
                  }`}
                />

                {/* Özellikler */}
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <div
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${
                          isPopular
                            ? 'bg-[#1bb8f5]/15'
                            : 'bg-[#1bb8f5]/8 dark:bg-[#1bb8f5]/10'
                        }`}
                      >
                        <Check
                          className={`h-2.5 w-2.5 ${
                            isPopular ? 'text-[#1bb8f5]' : 'text-[#1bb8f5]'
                          }`}
                          strokeWidth={2.5}
                        />
                      </div>
                      <span
                        className={`text-[12px] leading-snug ${
                          isPopular ? 'text-white/75' : 'text-[#666] dark:text-[#888]'
                        }`}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Satın Al CTA */}
                <div className="mt-4">
                  <div
                    className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-[13px] font-medium transition-all duration-200 ${
                      isPopular
                        ? 'bg-[#1bb8f5] text-white shadow-lg shadow-[#1bb8f5]/20 group-hover:shadow-xl group-hover:shadow-[#1bb8f5]/30 group-hover:bg-[#0ea5d6]'
                        : 'bg-[#0a0a0a] dark:bg-white text-white dark:text-[#0a0a0a] group-hover:bg-[#1bb8f5] dark:group-hover:bg-[#1bb8f5] dark:group-hover:text-white group-hover:shadow-lg group-hover:shadow-[#1bb8f5]/15'
                    }`}
                  >
                    Satın Al
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </div>
            </div>
          </motion.a>
        );
      })}

      {/* Alt bilgi */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.4 }}
        className="mt-1 space-y-4"
      >
        {/* Tek seferlik ödeme notu */}
        <div className="flex items-center justify-center gap-2 text-[12px] text-[#999] dark:text-[#555]">
          <div className="h-px w-6 bg-[#e5e5e5] dark:bg-[#333]" />
          <span>Tek seferlik ödeme · Abonelik yok</span>
          <div className="h-px w-6 bg-[#e5e5e5] dark:bg-[#333]" />
        </div>

        {/* Detaylı karşılaştırma linki */}
        <div className="text-center">
          <a
            href="https://okuratolyesi.com#pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#1bb8f5] hover:text-[#0ea5d6] dark:text-[#1bb8f5] dark:hover:text-[#4fcaff] transition-colors"
          >
            Detaylı karşılaştırma
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* WhatsApp iletişim butonu */}
        <motion.a
          href="https://wa.me/905317628230?text=Merhaba%2C%20paketler%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="group flex items-center gap-3 rounded-xl border border-[#e5e5e5]/80 dark:border-[#2a2a2a] bg-white/50 dark:bg-[#111]/50 backdrop-blur-xl p-3.5 transition-all duration-200 hover:border-[#25d366]/30 dark:hover:border-[#25d366]/20 hover:bg-[#25d366]/[0.03] dark:hover:bg-[#25d366]/[0.03] cursor-pointer"
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#25d366]/10 transition-colors duration-200 group-hover:bg-[#25d366]/15">
            <MessageCircle className="h-4 w-4 text-[#25d366]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-medium text-[#0a0a0a] dark:text-[#ededed]">
              Sorularınız mı var?
            </p>
            <p className="text-[11px] text-[#999] dark:text-[#666] mt-0.5">
              WhatsApp ile satış ekibimize ulaşın
            </p>
          </div>
          <div className="flex h-7 items-center rounded-lg bg-[#25d366]/10 px-2.5 text-[11px] font-medium text-[#25d366] transition-colors duration-200 group-hover:bg-[#25d366]/15">
            Yazın
          </div>
        </motion.a>
      </motion.div>
    </motion.div>
  );
};
